import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import Card from './Card';
import Button from './Button';
import { 
  ExclamationTriangleIcon, 
  ArrowPathIcon,
  HomeIcon,
  BugAntIcon
} from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
      eventId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error details for debugging
    this.logError(error, errorInfo);
  }

  private logError = (error: Error, errorInfo: ErrorInfo) => {
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      eventId: this.state.eventId
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Boundary Caught Error');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Error Details:', errorDetails);
      console.groupEnd();
    }

    // In production, you might want to send this to an error reporting service
    // like Sentry, LogRocket, or your own logging endpoint
    if (process.env.NODE_ENV === 'production') {
      // Example: Send to error reporting service
      // this.reportError(errorDetails);
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorFallback 
        error={this.state.error}
        eventId={this.state.eventId}
        onRetry={this.handleRetry}
        onGoHome={this.handleGoHome}
        onReload={this.handleReload}
      />;
    }

    return this.props.children;
  }
}

// Error Fallback Component
interface ErrorFallbackProps {
  error: Error | null;
  eventId: string | null;
  onRetry: () => void;
  onGoHome: () => void;
  onReload: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  eventId,
  onRetry,
  onGoHome,
  onReload
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const getErrorMessage = () => {
    // Always return a user-friendly localized message, never show technical details
    if (!error) return t('errors.unknownError', 'An unknown error occurred');

    // Log technical details to console for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
      console.error('Error details (hidden from user):', error.message, error.stack);
    }

    // Return user-friendly messages based on error type, but never the actual error message
    if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
      return t('errors.chunkLoadError', 'Failed to load page resources. Please refresh the page.');
    }

    if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      return t('errors.networkError', 'Network connection error. Please check your internet connection.');
    }

    if (error.message.includes('timeout')) {
      return t('errors.timeoutError', 'Request timed out. Please try again.');
    }

    // Generic error message - never show technical details
    return t('errors.unknownError', 'An unknown error occurred');
  };

  const getErrorTitle = () => {
    // Always return a localized title
    return t('errors.errorTitle', 'An error occurred');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="max-w-lg w-full">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
          
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {getErrorTitle()}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {getErrorMessage()}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={onRetry}
              className="flex items-center justify-center"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('errors.retry', 'Try Again')}
            </Button>
            
            <Button
              variant="outline"
              onClick={onGoHome}
              className="flex items-center justify-center"
            >
              <HomeIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('errors.goHome', 'Go Home')}
            </Button>
            
            <Button
              variant="outline"
              onClick={onReload}
              className="flex items-center justify-center"
            >
              <BugAntIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('errors.reload', 'Reload Page')}
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            {t('errors.contactSupport', 'If this problem persists, please contact support.')}
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ErrorBoundary;